import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import type { NavContentComponent } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

import AddIcon from '@material-ui/icons/Add';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PublicIcon from '@material-ui/icons/Public';
import TimelineIcon from '@material-ui/icons/Timeline';
import StorageIcon from '@material-ui/icons/Storage';
import { Capability, canRunAnyWorkflow } from '@internal/datasphere-personas';
import { usePersonaSession } from '@internal/datasphere-personas/react';

// Named component rather than an inline arrow: it calls usePersonaSession, and
// react-hooks/rules-of-hooks only recognises hooks inside named components.
// Exported for its test.
export const SidebarComponent: NavContentComponent = ({ navItems }) => {
  const persona = usePersonaSession();
  const nav = navItems.withComponent(item => (
    <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
  ));

  // Skipped items
  nav.take('page:search'); // Using search modal instead

  // Presentation only — the backend refuses these routes for personas that
  // cannot use them (ADR-0010).
  const canInvokeWorkflow = canRunAnyWorkflow(persona.capabilities);
  const canSeeRequests =
    persona.can(Capability.RequestReadOwn) ||
    persona.can(Capability.RequestReadAll);
  const isAdmin = persona.can(Capability.AdminAccess);

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Workspace" icon={<MenuIcon />}>
        <SidebarItem icon={() => <PublicIcon />} to="/" text="Overview" />
        <SidebarItem
          icon={() => <TimelineIcon />}
          to="/roadmap"
          text="Roadmap"
        />
      </SidebarGroup>
      {canInvokeWorkflow || canSeeRequests ? (
        <SidebarGroup label="Requests" icon={<AssignmentIcon />}>
          {canInvokeWorkflow ? (
            <SidebarItem
              icon={() => <AddIcon />}
              to="/create"
              text="Request a Service"
            />
          ) : null}
          {canSeeRequests ? (
            <SidebarItem
              icon={() => <AssignmentIcon />}
              to="/spoke-requests"
              text="All Requests"
            />
          ) : null}
        </SidebarGroup>
      ) : null}
      {isAdmin ? (
        <SidebarGroup label="Administration" icon={<StorageIcon />}>
          <SidebarItem
            icon={() => <StorageIcon />}
            to="/database-admin"
            text="Database administration"
          />
        </SidebarGroup>
      ) : null}
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        {nav.take('page:app-visualizer')}
        {nav.take('page:user-settings')}
      </SidebarGroup>
    </Sidebar>
  );
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: SidebarComponent,
  },
});
